/* ==========================================================================
   Hero slider (sections/hero-slider.liquid)

   Behaviour
   - Video slides advance when the video ends; image slides after N seconds.
   - Only the active slide (and the next one) ever have a video src attached,
     so three slides never download or play three videos at once.
   - Autoplay is muted + playsinline so iOS Safari allows it. If the browser
     still refuses (data saver, low power mode), the poster image stays up and
     the slide falls back to the timed advance.
   - Manual: swipe, dots, keyboard left/right. Loops past the last.
   - prefers-reduced-motion: no video autoplay, no auto-advance, no animation.
     The poster still shows and manual navigation still works.
   ========================================================================== */
(function () {
  'use strict';

  var PLAY_WATCHDOG_MS = 1600;

  function DnaHeroSlider(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-dhs-viewport]');
    this.track = root.querySelector('[data-dhs-track]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-dhs-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-dhs-dot]'));
    this.live = root.querySelector('[data-dhs-live]');
    this.count = this.slides.length;

    if (!this.track || this.count === 0) return;

    this.index = 0;
    this.timer = null;
    this.watchdog = null;
    this.inView = true;
    this.imageMs = (parseFloat(root.getAttribute('data-image-seconds')) || 5) * 1000;

    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reduced = mq.matches;
    var self = this;
    if (mq.addEventListener) {
      mq.addEventListener('change', function (e) {
        self.reduced = e.matches;
        if (self.reduced) self.stopNativeAutoplay();
        self.restart();
      });
    }

    this.measureOffset();
    window.addEventListener('resize', function () { self.measureOffset(); });

    // The first slide carries a native `autoplay` attribute so iOS Safari
    // starts it without waiting for us. Under reduced motion we have to take
    // that back before the browser acts on it.
    if (this.reduced) this.stopNativeAutoplay();

    this.bind();
    this.apply(false);
    this.restart();
    root.__dhs = this;
    root.setAttribute('data-dhs-ready', 'true');
  }

  /* How much of the screen is taken by whatever sits above the hero (the
     theme's sticky header + category nav). Only applied when the merchant has
     "Fit below the sticky header" on. */
  DnaHeroSlider.prototype.measureOffset = function () {
    if (!this.root.classList.contains('dhs--fit')) return;
    var top = this.root.getBoundingClientRect().top + (window.pageYOffset || 0);
    if (!isFinite(top) || top < 0) top = 0;
    if (top > window.innerHeight * 0.5) top = 0;   // hero isn't at the top of the page
    this.root.style.setProperty('--dhs-offset', Math.round(top) + 'px');
  };

  DnaHeroSlider.prototype.stopNativeAutoplay = function () {
    this.slides.forEach(function (slide) {
      var video = slide.querySelector('[data-dhs-video]');
      if (!video) return;
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
      video.classList.remove('is-playing');
    });
  };

  DnaHeroSlider.prototype.rtl = function () {
    return getComputedStyle(this.root).direction === 'rtl';
  };

  /* ---------- wiring ---------- */

  DnaHeroSlider.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (e) {
      var dot = e.target.closest('[data-dhs-dot]');
      if (dot) self.go(parseInt(dot.getAttribute('data-dhs-dot'), 10));
    });

    // Keyboard navigation for the focused slider. This is the accessible
    // equivalent of a swipe — unrelated to any on-screen arrow button.
    this.root.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var forward = self.rtl() ? e.key === 'ArrowLeft' : e.key === 'ArrowRight';
      e.preventDefault();
      self.go(self.index + (forward ? 1 : -1));
    });

    // Each video announces its own end. Only the active one may advance.
    this.slides.forEach(function (slide, i) {
      var video = slide.querySelector('[data-dhs-video]');
      if (!video) return;
      video.addEventListener('ended', function () {
        if (i === self.index && !self.reduced) self.go(self.index + 1);
      });
      video.addEventListener('playing', function () {
        video.classList.add('is-playing');
        self.clearTimer();
        self.clearWatchdog();
      });
      video.addEventListener('error', function () {
        if (i === self.index) self.startTimer(self.imageMs);
      });
    });

    if (this.count > 1) this.bindDrag();

    // Don't burn CPU or skip slides while the hero is off screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          self.inView = entry.isIntersecting && entry.intersectionRatio > 0.2;
          if (self.inView) self.restart(); else self.suspend();
        });
      }, { threshold: [0, 0.25] }).observe(this.root);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.suspend(); else if (self.inView) self.restart();
    });
  };

  DnaHeroSlider.prototype.bindDrag = function () {
    var self = this;
    var startX = 0, startY = 0, dx = 0, dragging = false, decided = false, id = null;

    var down = function (e) {
      if (e.button != null && e.button !== 0) return;
      if (e.target.closest('a, button')) return;
      id = e.pointerId != null ? e.pointerId : null;
      startX = e.clientX != null ? e.clientX : e.touches[0].clientX;
      startY = e.clientY != null ? e.clientY : e.touches[0].clientY;
      dx = 0; dragging = true; decided = false;
    };

    var move = function (e) {
      if (!dragging) return;
      var x = e.clientX != null ? e.clientX : e.touches[0].clientX;
      var y = e.clientY != null ? e.clientY : e.touches[0].clientY;
      var ddx = x - startX, ddy = y - startY;
      if (!decided) {
        if (Math.abs(ddx) < 8 && Math.abs(ddy) < 8) return;
        // Vertical intent: let the page scroll, abandon the drag.
        if (Math.abs(ddy) > Math.abs(ddx)) { dragging = false; return; }
        decided = true;
        self.track.classList.add('is-dragging');
        self.suspend();
      }
      dx = ddx;
      if (e.cancelable) e.preventDefault();
      var pct = (dx / self.viewport.offsetWidth) * 100;
      self.setTransform(self.offset() + pct);
    };

    var up = function () {
      if (!dragging) return;
      dragging = false;
      self.track.classList.remove('is-dragging');
      if (!decided) return;
      var threshold = Math.max(40, self.viewport.offsetWidth * 0.12);
      var dir = self.rtl() ? 1 : -1;
      if (Math.abs(dx) > threshold) self.go(self.index + (dx < 0 ? -dir : dir));
      else { self.apply(true); self.restart(); }
      dx = 0;
    };

    if (window.PointerEvent) {
      this.viewport.addEventListener('pointerdown', down);
      this.viewport.addEventListener('pointermove', move, { passive: false });
      this.viewport.addEventListener('pointerup', up);
      this.viewport.addEventListener('pointercancel', up);
      this.viewport.addEventListener('lostpointercapture', up);
    } else {
      this.viewport.addEventListener('touchstart', down, { passive: true });
      this.viewport.addEventListener('touchmove', move, { passive: false });
      this.viewport.addEventListener('touchend', up);
      this.viewport.addEventListener('touchcancel', up);
    }
  };

  /* ---------- movement ---------- */

  DnaHeroSlider.prototype.offset = function () {
    return (this.rtl() ? 1 : -1) * this.index * 100;
  };

  DnaHeroSlider.prototype.setTransform = function (pct) {
    this.track.style.transform = 'translate3d(' + pct + '%, 0, 0)';
  };

  DnaHeroSlider.prototype.go = function (i) {
    var next = ((i % this.count) + this.count) % this.count; // wraps both ways
    var changed = next !== this.index;
    this.index = next;
    this.apply(true);
    if (changed) this.announce();
    this.restart();
  };

  DnaHeroSlider.prototype.apply = function (animate) {
    var self = this;
    if (!animate) {
      this.track.classList.add('is-dragging');   // also means "no transition"
      this.setTransform(this.offset());
      void this.track.offsetWidth;               // flush before re-enabling
      this.track.classList.remove('is-dragging');
    } else {
      this.setTransform(this.offset());
    }

    this.slides.forEach(function (slide, i) {
      var active = i === self.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.querySelectorAll('a, button').forEach(function (el) {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });

    this.dots.forEach(function (dot, i) {
      dot.setAttribute('aria-current', i === self.index ? 'true' : 'false');
    });

    this.prepareMedia();
  };

  DnaHeroSlider.prototype.announce = function () {
    if (!this.live) return;
    var slide = this.slides[this.index];
    var label = slide.getAttribute('data-dhs-title') || '';
    var position = (this.index + 1) + ' / ' + this.count;
    this.live.textContent = label ? label + ' (' + position + ')' : position;
  };

  /* ---------- media ---------- */

  /* Attach a src only to the active slide and the one after it; strip it
     from everything else so idle videos hold no buffer. */
  DnaHeroSlider.prototype.prepareMedia = function () {
    var self = this;
    var nextIndex = (this.index + 1) % this.count;

    this.slides.forEach(function (slide, i) {
      var video = slide.querySelector('[data-dhs-video]');
      if (!video) return;
      var src = video.getAttribute('data-src');
      if (!src) return;

      if (i === self.index || (i === nextIndex && self.count > 1)) {
        if (video.getAttribute('src') !== src) {
          video.setAttribute('src', src);
          video.preload = i === self.index ? 'auto' : 'metadata';
          video.load();
        }
      } else if (video.getAttribute('src')) {
        video.classList.remove('is-playing');
        video.removeAttribute('src');
        video.load();
      }
    });
  };

  DnaHeroSlider.prototype.playActive = function () {
    var self = this;
    var slide = this.slides[this.index];
    var video = slide.querySelector('[data-dhs-video]');

    // Stop anything that isn't the current slide.
    this.slides.forEach(function (other, i) {
      if (i === self.index) return;
      var v = other.querySelector('[data-dhs-video]');
      if (v && !v.paused) { v.pause(); v.classList.remove('is-playing'); }
    });

    if (!video || !video.getAttribute('data-src')) {
      this.startTimer(this.imageMs);           // image slide
      return;
    }

    if (this.reduced) return;                  // poster only, no auto-advance

    video.muted = true;                        // belt and braces for iOS
    video.setAttribute('muted', '');
    try { video.currentTime = 0; } catch (e) { /* not seekable yet */ }

    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () { self.startTimer(self.imageMs); });
    }

    // If it hasn't actually started shortly, treat the slide as poster-only.
    this.clearWatchdog();
    this.watchdog = window.setTimeout(function () {
      if (video.paused || video.readyState < 2) self.startTimer(self.imageMs);
    }, PLAY_WATCHDOG_MS);
  };

  /* ---------- timing ---------- */

  DnaHeroSlider.prototype.startTimer = function (ms) {
    var self = this;
    this.clearTimer();
    if (this.reduced || this.count < 2 || !this.inView || document.hidden) return;
    this.timer = window.setTimeout(function () { self.go(self.index + 1); }, ms);
  };

  DnaHeroSlider.prototype.clearTimer = function () {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  };

  DnaHeroSlider.prototype.clearWatchdog = function () {
    if (this.watchdog) { clearTimeout(this.watchdog); this.watchdog = null; }
  };

  DnaHeroSlider.prototype.restart = function () {
    this.clearTimer();
    this.clearWatchdog();
    if (!this.inView || document.hidden) return;
    this.playActive();
  };

  DnaHeroSlider.prototype.suspend = function () {
    this.clearTimer();
    this.clearWatchdog();
    var video = this.slides[this.index].querySelector('[data-dhs-video]');
    if (video && !video.paused) video.pause();
  };

  /* ---------- boot ---------- */

  function init(scope) {
    (scope || document).querySelectorAll('[data-dhs]:not([data-dhs-ready])')
      .forEach(function (el) { new DnaHeroSlider(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: re-init on section reload, jump to the block being edited.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest('[data-dhs]');
    if (!root || !root.__dhs) return;
    root.__dhs.go(parseInt(e.target.getAttribute('data-index'), 10) || 0);
  });

  window.DnaHeroSlider = DnaHeroSlider;
})();
